// public/js/features/manual/manual-data.js
// Content for the device manual (wiki). One entry per Superb-branded product.
// Feature bullets are reused from the subscription pricing catalogue (same keys),
// so the manual and the price list never drift. Everything customer-facing uses
// the Superb brand name only - never the underlying vendor model.
//
// Specs are taken from the vendors' official datasheets (values verified, cited
// in docs/device-manual.md), but presented brand-free. `dimensions` drives a
// self-drawn, brand-free measurement sketch (box {l,w,h} or disc {d,h}).
import { BRANDED_PRODUCTS, BRANDED_ORDER } from '../devices/branded-models.js';
import { ALL_PRODUCTS } from '../subscription/pricing-data.js';

const pricing = Object.fromEntries(ALL_PRODUCTS.map(p => [p.key, p]));

const CONTENT = {
  tracepro: {
    tagline: 'Airworthy active tracker for aircraft and air cargo.',
    image: 'tracepro-normal.png',
    overview: 'The TracePro is our flagship active tracker: it finds its own position by GPS and reports it worldwide over the cellular network. It is airworthy - built and certified for use on aircraft - and detects flight automatically, pausing transmission in the air. On the ground it doubles as a gateway, relaying the readings of nearby Superb tags and sensors on the same asset.',
    useCases: ['Aircraft parts and rotables that fly with the asset', 'Air cargo and ULD containers', 'High-value assets that cross borders and need worldwide coverage'],
    specs: [
      ['Type', 'Active GPS + cellular tracker'],
      ['Airworthiness', 'DO-160G tested; certified on 20+ airlines'],
      ['Positioning', 'GPS / Galileo / GLONASS / BeiDou (~1 m) + Wi-Fi & cell fallback'],
      ['Coverage', '4G Cat-1bis + 2G, worldwide'],
      ['Gateway', 'Bluetooth 5.2 - relays paired tags & sensors'],
      ['Extra sensors', 'Barometer, accelerometer, automatic flight detection'],
      ['Size / weight', '108 x 86 x 30 mm, 170 g'],
      ['Protection', 'IP67, -30 to +60 C'],
      ['Battery', '3 x AA lithium, user-replaceable'],
    ],
    dimensions: { l: 108, w: 86, h: 30 },
    battery: true,
  },
  locatepro: {
    tagline: 'Rugged active tracker for ground assets.',
    image: 'locatepro-normal.png',
    overview: 'The LocatePro is a rugged, self-powered active tracker for assets that stay on the ground: ground support equipment, tools, trailers and containers. Like the TracePro it reports its own GPS position over cellular and can act as a gateway for paired tags and sensors - but it is optimised for very long battery life rather than flight.',
    useCases: ['Ground support equipment (GSE)', 'Tool boxes, trailers and containers', 'Any non-airworthy asset that needs its own live position'],
    specs: [
      ['Type', 'Active GPS + cellular tracker'],
      ['Positioning', 'GPS / GLONASS / Galileo / BeiDou (~2 m)'],
      ['Coverage', 'LTE-M / NB-IoT, worldwide'],
      ['Gateway', 'Relays paired tags & sensors (Bluetooth variant)'],
      ['Extra sensors', '3-axis accelerometer (impact, tip, rotation)'],
      ['Size / weight', '108 x 86 x 30 mm, 166 g'],
      ['Protection', 'IP68 & IK07 impact, -30 to +60 C'],
      ['Battery', '3 x AA lithium, user-replaceable, up to 10 yr'],
    ],
    dimensions: { l: 108, w: 86, h: 30 },
    battery: true,
  },
  smart: {
    tagline: 'Low-cost tag for passive tracking.',
    image: 'smart-normal.png',
    overview: 'The Smart+ is a small, inexpensive tag that does not track itself - instead it is located by nearby Superb trackers and gateways. Attach it to lower-value assets to know which tracker (and therefore which asset or location) it is with, and get an alert if it goes missing.',
    useCases: ['Passive tracking of many low-value items', 'Presence supervision on a parent asset', 'Kits and consumables that move between assets'],
    specs: [
      ['Type', 'Passive Bluetooth tag'],
      ['Located by', 'Nearby Superb trackers / gateways'],
      ['Supervision', 'Leaves-asset alert; optional tether warning'],
      ['Battery', 'Multi-year coin cell'],
    ],
    battery: false,
    batteryNote: 'Multi-year battery life on a standard coin cell; nothing you configure changes it.',
  },
  temp: {
    tagline: 'Wireless temperature sensor.',
    image: 'temp-normal.png',
    overview: 'The Temp+ measures temperature and sends it to the platform through the nearest Superb tracker or gateway. You get live readings, history charts and threshold alerts - for example a cargo-hold overheat warning - with anti-flap smoothing so a brief spike does not spam you.',
    useCases: ['Temperature-sensitive cargo and parts', 'Cold-chain and controlled storage', 'Component temperature monitoring'],
    specs: [
      ['Type', 'Wireless Bluetooth temperature sensor'],
      ['Measuring range', '-40 to +85 C'],
      ['Accuracy / resolution', '+/-0.4 C max, 0.01 C'],
      ['Reports via', 'Nearest Superb tracker / gateway'],
      ['Radio range', 'Up to 500 m open field'],
      ['Data', 'Live readings, history charts, threshold alerts'],
      ['Size / weight', 'D 57 x 20 mm, 41 g'],
      ['Housing', 'POM, food-contact compatible'],
      ['Mounting', 'Central D6 screw hole, or adhesive pad / holder'],
      ['Protection', 'IP65'],
      ['Battery', 'Sealed lithium, ~11 yr as configured'],
    ],
    dimensions: { d: 57, h: 20 },
    drawing: 'discPuck',
    battery: false,
    batteryNote: 'Sealed battery, not replaceable. About 11 years at the 5 s reporting rate and full transmit power we configure; the cell itself is good for 20. Cold shortens it markedly - around 7 years at -18 C.',
  },
  humidity: {
    tagline: 'Wireless humidity sensor.',
    image: 'humidity-normal.png',
    overview: 'The Humidity+ measures relative humidity and reports it through the nearest Superb tracker or gateway, with the same live charts, history and threshold alerts as the Temp+. Use it wherever moisture matters to the asset.',
    useCases: ['Moisture-sensitive parts and cargo', 'Storage and container condition monitoring', 'Corrosion-risk environments'],
    specs: [
      ['Type', 'Wireless Bluetooth humidity sensor'],
      ['Measuring range', '0 to 100 % RH'],
      ['Accuracy / resolution', '+/-2.5 % to 90 % RH, +/-3.5 % above; 0.01 %'],
      ['Reports via', 'Nearest Superb tracker / gateway'],
      ['Radio range', 'Up to 500 m open field'],
      ['Data', 'Live readings, history charts, threshold alerts'],
      ['Size / weight', 'D 57 x 20 mm, 41 g'],
      ['Housing', 'POM, food-contact compatible'],
      ['Mounting', 'Central D6 screw hole, or adhesive pad / holder'],
      ['Protection', 'IP65'],
      ['Battery', 'Sealed lithium, ~11 yr as configured'],
    ],
    dimensions: { d: 57, h: 20 },
    drawing: 'discPuck',
    battery: false,
    batteryNote: 'Sealed battery, not replaceable. About 11 years at the 5 s reporting rate and full transmit power we configure; the cell itself is good for 20. Cold shortens it markedly - around 7 years at -18 C.',
  },
  engine: {
    tagline: 'Engine-pairing supervision beacon.',
    image: 'ibs09r-normal.png',
    overview: 'The Engine+ pairs a beacon to an engine or engine stand and supervises that the two stay together. If the engine and its stand separate - or the beacon leaves its home gateway - you get an alert. It underpins the engine-stand management workflow. A built-in Time-of-Flight sensor also measures distance, so a stand can report loaded or empty on its own.',
    useCases: ['Engine and engine-stand pairing', 'Supervised "must stay together" relationships', 'Loaded / empty detection by distance', 'Anchoring a beacon to a specific home gateway'],
    specs: [
      ['Type', 'Bluetooth distance / supervision beacon'],
      ['Function', 'Pairs an engine to its stand'],
      ['Supervision', 'Separation alert; home-gateway anchoring'],
      ['Distance sensing', '1 - 300 cm (Time-of-Flight)'],
      ['Size', '66.8 x 34.8 x 8.4 mm'],
      ['Protection', 'IPX7 waterproof'],
      ['Battery', 'CR2450 coin cell, user-replaceable'],
    ],
    dimensions: { l: 66.8, w: 34.8, h: 8.4 },
    battery: false,
    batteryNote: 'Multi-year battery on a replaceable CR2450 coin cell.',
  },
  shock: {
    tagline: 'Impact and rough-handling detection.',
    overview: 'Shock+ is not a separate device - it is an impact detector activated inside a TracePro or LocatePro. It flags impacts and rough handling and delivers a shock alert to the browser and push, so mishandling is caught the moment it happens.',
    useCases: ['Detecting drops and rough handling', 'Fragile or calibration-sensitive assets', 'Proof of careful (or careless) transport'],
    specs: [
      ['Type', 'Feature inside TracePro / LocatePro'],
      ['Detects', 'Impacts and rough handling'],
      ['Alerts', 'Browser + push, in real time'],
      ['Extra hardware', 'None - built into the tracker'],
    ],
    battery: false,
  },
};

// Merge brand + pricing features + manual content into the render model.
export const MANUAL = BRANDED_ORDER.map(key => {
  const brand = BRANDED_PRODUCTS[key];
  const c = CONTENT[key] || {};
  return {
    ...brand,
    tagline: c.tagline || pricing[key]?.desc || '',
    overview: c.overview || '',
    useCases: c.useCases || [],
    features: (pricing[key]?.details || []).map(d => (typeof d === 'string' ? d : d.html)).filter(Boolean),
    hasHtmlFeatures: (pricing[key]?.details || []).some(d => typeof d !== 'string'),
    specs: c.specs || [],
    dimensions: c.dimensions || null,
    image: c.image || brand.image || null,
    battery: !!c.battery,
    batteryNote: c.batteryNote || null,
  };
});

export const MANUAL_BY_SLUG = Object.fromEntries(MANUAL.map(m => [m.slug, m]));

// Grouping for the table of contents.
export const MANUAL_GROUPS = [
  { title: 'Trackers', keys: ['tracepro', 'locatepro'] },
  { title: 'Tags & sensors', keys: ['smart', 'temp', 'humidity', 'engine'] },
  { title: 'Features', keys: ['shock'] },
];

// ── Internal operations: preparing trackers for shipment (docs/tracker-shipping.md) ──
// The bench process between "order received" and "parcel handed to the carrier".
// Platform staff only (the page is gated in manual-page.js). Steps are numbered
// continuously across phases and MUST stay in order - several only make sense
// because of the one before them. `text` may contain inline HTML: fixed values
// (APN, wait time, torque, label placement) are wrapped in <b> so they stand out.
export const SHIPPING_PROCESS = {
  slug: 'shipping',
  title: 'Preparing trackers for shipment',
  intro: 'What happens on the bench between "order received" and "parcel handed to the carrier". Follow the steps in order - several only make sense because of the one before them.',
  phases: [
    { title: 'Configuration', icon: 'fa-sliders', steps: [
      { text: 'Set the <b>admin template for all trackers to Superb</b> (tracker configuration).' },
    ] },
    { title: 'Pairing hardware', icon: 'fa-sim-card', steps: [
      { text: 'Take the trackers and the SIM cards from the shelf.' },
      { text: 'Pair them: <b>one SIM card with one tracker</b>, and keep each pair together from here on.' },
      { text: 'Create an <b>activation label per tracker/SIM combination</b>.' },
      { text: 'Print the labels and apply them - <b>on the underside</b>.' },
      { text: '<b>Activate with the sticker.</b>' },
    ] },
    { title: 'Bringing the tracker up', icon: 'fa-power-off', steps: [
      { text: 'Insert the batteries.' },
      { text: 'In the <b>provisioning tool</b>, set the <b>APN to <code>shared.m2m.ch</code></b>.' },
      { text: 'Remove the batteries.' },
      { text: 'Wait <b>30 seconds</b>, then re-insert the batteries.' },
    ] },
    { title: 'Closing up', icon: 'fa-screwdriver-wrench', steps: [
      { text: 'Screw the housing together - <b>Hilti, torque setting 6, Torx 15</b>.' },
      { text: 'Attach the magnets.' },
      { text: 'Apply the <b>Superb sticker</b> - <b>on top</b>.' },
    ] },
    { title: 'Booking and dispatch', icon: 'fa-truck-fast', steps: [
      { text: '<b>Scan the SIM and the serial number</b> against the order.' },
      { text: 'Ship.' },
    ] },
  ],
  notes: [
    'Steps 9-10 are a deliberate power cycle after the APN change, not a mistake: the tracker has to come up fresh on the new APN.',
    'Label placement is fixed: activation label on the <b>bottom</b>, Superb sticker on the <b>top</b>. The customer sees the top.',
    'Step 14 is what ties the physical unit to the customer in the platform (distribution / order). A tracker that ships without this scan is invisible to the delivery tracking.',
  ],
};
